<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import useApi from '../../Composables/useApi'
import { useI18n } from 'vue-i18n'
import BlockContent from '../UI/Content/BlockContent.vue'
import PaymentList from './PaymentList.vue'
import CreditNoteList from './CreditNoteList.vue'
import DepositList from './DepositList.vue'
import InvoiceItemList from './InvoiceItemList.vue'
import type { ElectronicInvoiceStatusHistory, Invoice } from '../../../../Types/invoice'
import InvoiceInformationBlock from './InvoiceInformationBlock.vue'
import TabMenu from '../UI/TabMenu.vue'
import type { Tab } from '../../../../Types/tab'
import { useElectronicInvoiceStatusHistory } from '../../../../Composables/Invoice/useElectronicInvoiceStatusHistory.ts'
import ElectronicInvoiceStatusHistoryList from './ElectronicInvoiceStatusHistoryList.vue'
import ElectronicInvoiceAnomalyFormDialog from './ElectronicInvoiceAnomalyFormDialog.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import { useInvoice } from '../../../../Composables/Invoice/useInvoice.ts'
import InvoiceAllocableItemList from './InvoiceAllocableItemList.vue'

type ModalDialogType = InstanceType<typeof ModalDialog>

const { n, t } = useI18n()
const { getJSON } = useApi()
const { getHistories } = useElectronicInvoiceStatusHistory(() => invoice.value?.id ?? null)
const { validate, generatePayment } = useInvoice(() => Number(invoice.value?.id))

const props = defineProps({
  id: { type: String, required: true },
})

const modalRef = ref<ModalDialogType | null>(null)
const invoice = ref<Invoice | null>(null)
const histories = ref<ElectronicInvoiceStatusHistory[]>([])
const loading = ref(false)
const validateLoading = ref(false)
const generatePaymentLoading = ref(false)
const tab = ref<Tab | null>(null)

const tabs = computed(() => [
  {
    key: 'information',
    show: true,
    title: t('invoice.tab.information'),
  },
  {
    key: 'electronicInvoiceStatusHistory',
    show: !!invoice.value?.isElectronicInvoice,
    title: t('invoice.tab.electronicInvoiceStatusHistory'),
  },
])
const showTabs = computed(() => tabs.value.filter((tab) => tab.show).length > 1)
const displayAnomalyForm = ref(false)

async function onAnomalySaved() {
  setAnomalyFormDisplay(false)
  await reloadInvoice()
  await loadHistories()
}

async function loadHistories(): Promise<void> {
  histories.value = await getHistories()
}
async function fetchInvoice(invoiceId: number): Promise<Invoice> {
  return getJSON(`invoices/${invoiceId}`) as Invoice
}

async function openModal(invoiceId: number) {
  loading.value = true
  try {
    invoice.value = await fetchInvoice(invoiceId)
    tab.value = tabs.value[0] ?? null
    modalRef.value?.open()
  } catch (e) {
    console.error('Impossible de charger la facture :', e)
  } finally {
    loading.value = false
  }
}

async function reloadInvoice() {
  if (!invoice.value) return
  try {
    invoice.value = await fetchInvoice(invoice.value.id)
  } catch (e) {
    console.error('Impossible de recharger la facture :', e)
  }
}

async function validateSupplierInvoice() {
  validateLoading.value = true
  try {
    await validate()
  } finally {
    await reloadInvoice()
    validateLoading.value = false
  }
}

async function generatePaymentSupplierInvoice() {
  generatePaymentLoading.value = true
  try {
    await generatePayment()
  } finally {
    await reloadInvoice()
    generatePaymentLoading.value = false
  }
}

function closeModal() {
  invoice.value = null
  setAnomalyFormDisplay(false)
  tab.value = tabs.value[0] ?? null
  modalRef.value?.close(false)
}

function setAnomalyFormDisplay(display: boolean) {
  displayAnomalyForm.value = display
}

watch(tab, (newTab, oldTab) => {
  if (newTab?.key === 'electronicInvoiceStatusHistory') {
    loadHistories()
  }
})

defineExpose({ openModal, closeModal })
</script>

<template>
  <div>
    <ModalDialog
      :id-modal-page="props.id"
      ref="modalRef"
      :id="`${props.id}-modal`"
      :fullscreen="true"
      :title="invoice ? invoice.name : ''"
      @icon-close="closeModal"
    >
      <template #body>
        <div v-if="loading">{{ t('invoice.label.loading') }}</div>
        <div v-else-if="invoice">
          <TabMenu v-if="showTabs" v-model="tab" :tabs="tabs" :box-shadow="false" />
          <div v-if="tab?.key === 'information'">
            <div class="row pl-30">
              <ButtonAll
                v-if="invoice.isValidable && !invoice.validatedAt"
                icon="euro"
                :loading="validateLoading"
                margin="no"
                label="invoice.electronic_invoice.actions.validate"
                type="warning"
                @click="validateSupplierInvoice()"
              />
              <ButtonAll
                v-if="invoice.isElectronicInvoice && !invoice.validatedAt && !invoice.canceledAt"
                icon="warning"
                margin="no"
                class="ml-2"
                label="invoice.electronic_invoice.actions.addAnomaly"
                type="danger"
                @click="setAnomalyFormDisplay(true)"
              />
            </div>
            <ElectronicInvoiceAnomalyFormDialog
              v-if="displayAnomalyForm"
              :invoice-id="invoice.id"
              @close="setAnomalyFormDisplay(false)"
              @save="onAnomalySaved"
            />
            <div class="row">
              <div class="col-12 col-md-5">
                <BlockContent color="primary" inner-bloc-class="border-contract-yellow">
                  <h4 class="mb-3">{{ t('invoice.title.information') }}</h4>
                  <InvoiceInformationBlock :invoice="invoice" @updated="reloadInvoice" />
                </BlockContent>
              </div>
              <div class="col-12 col-md-7">
                <BlockContent color="primary" inner-bloc-class="border-contract-yellow">
                  <h4 class="mb-3">{{ t('invoice.title.purchaseOrder') }}</h4>
                  <InvoiceItemList :invoice="invoice" />
                  <InvoiceAllocableItemList
                    v-if="!invoice.validatedAt && !invoice.deposit && invoice.allocableItems?.length > 0"
                    :invoice="invoice"
                    @item-created="reloadInvoice"
                  />
                </BlockContent>
                <BlockContent color="primary" inner-bloc-class="border-contract-yellow">
                  <h4 class="mb-3">{{ t('invoice.title.payment') }}</h4>
                  <div>
                    <ButtonAll
                      v-if="invoice.validatedAt"
                      label="invoice.electronic_invoice.actions.generatePayment"
                      margin="no"
                      icon="euro"
                      type="warning"
                      :loading="generatePaymentLoading"
                      @click="generatePaymentSupplierInvoice()"
                    />
                  </div>
                  <PaymentList :payments="invoice.payments" />
                </BlockContent>
                <BlockContent color="primary" inner-bloc-class="border-contract-yellow">
                  <h4 class="mb-3">{{ t('invoice.title.creditNote') }}</h4>
                  <CreditNoteList :invoices="invoice.creditNotes" />
                </BlockContent>
                <BlockContent color="primary" inner-bloc-class="border-contract-yellow">
                  <h4 class="mb-3">{{ t('invoice.title.deposit') }}</h4>
                  <DepositList :deposits="invoice.deposits" />
                </BlockContent>
              </div>
            </div>
          </div>

          <div class="row" v-if="tab?.key === 'electronicInvoiceStatusHistory'">
            <BlockContent class="col-12" color="primary" inner-bloc-class="border-contract-yellow">
              <h4 class="mb-3">{{ t('invoice.title.electronicInvoiceStatusHistory') }}</h4>
              <ElectronicInvoiceStatusHistoryList :histories="histories" />
            </BlockContent>
          </div>
        </div>
      </template>
    </ModalDialog>
  </div>
</template>
